import React from 'react';
import { Download, Trash2, RotateCcw, RefreshCw } from 'lucide-react';
import { Checkbox } from '../Checkbox';
import './SelectionBar.css';

export interface SelectionBarProps {
  /** Number of currently selected items */
  selectedCount: number;
  /** Total number of selectable items (for "select all" functionality) */
  totalCount?: number;
  /** Handler when cancel/exit is clicked */
  onCancel: () => void;
  /** Handler when "select all" is clicked */
  onSelectAll?: () => void;
  /** Handler when "deselect all" is clicked */
  onDeselectAll?: () => void;
  /** Handler for download action */
  onDownload?: () => void;
  /** Handler for delete action */
  onDelete?: () => void;
  /** Handler for restore action (when in deleted mode) */
  onRestore?: () => void;
  /** Handler for reprocess action */
  onReprocess?: () => void;
  /** Whether showing deleted documents (shows restore instead of delete) */
  showDeleted?: boolean;
  /** Label format for selected count (default: "{count} selected") */
  selectedLabel?: string;
  /** Cancel button label */
  cancelLabel?: string;
  /** Select all label */
  selectAllLabel?: string;
  /** Download button aria-label */
  downloadLabel?: string;
  /** Delete button aria-label */
  deleteLabel?: string;
  /** Restore button aria-label */
  restoreLabel?: string;
  /** Reprocess button aria-label */
  reprocessLabel?: string;
  /** Optional class name */
  className?: string;
  /** Whether to show the "select all matching" banner */
  showSelectAllBanner?: boolean;
  /** Handler when user clicks "select all matching" in the banner */
  onSelectAllMatching?: () => void;
  /** Whether all matching docs are selected (shows "all selected" confirmation) */
  allMatchingSelected?: boolean;
  /** Total matching documents across all pages */
  totalMatching?: number;
  /** Whether loading all matching IDs */
  loadingAllIds?: boolean;
  /** Label for "Select all {count} matching documents" banner */
  selectAllMatchingLabel?: string;
  /** Label for "All {count} documents selected" confirmation */
  allSelectedConfirmLabel?: string;
}

/**
 * Selection bar shown when in document selection mode.
 * Displays count of selected items with select all / cancel actions.
 * Shows a Gmail-style "Select all matching" banner when all on-page items are selected.
 */
export const SelectionBar: React.FC<SelectionBarProps> = ({
  selectedCount,
  totalCount,
  onCancel,
  onSelectAll,
  onDeselectAll,
  onDownload,
  onDelete,
  onRestore,
  onReprocess,
  showDeleted = false,
  selectedLabel = '{count} selected',
  cancelLabel = 'Cancel',
  selectAllLabel = 'Select all',
  downloadLabel = 'Download selected',
  deleteLabel = 'Delete selected',
  restoreLabel = 'Restore selected',
  reprocessLabel = 'Reprocess selected',
  className = '',
  showSelectAllBanner = false,
  onSelectAllMatching,
  allMatchingSelected = false,
  totalMatching,
  loadingAllIds = false,
  selectAllMatchingLabel = 'Select all {count} matching documents',
  allSelectedConfirmLabel = 'All {count} documents selected.',
}) => {
  const allSelected = totalCount !== undefined && selectedCount === totalCount && totalCount > 0;
  const someSelected = selectedCount > 0 && !allSelected;
  const hasSelection = selectedCount > 0;

  const handleSelectAllClick = () => {
    if (allSelected && onDeselectAll) {
      onDeselectAll();
    } else if (onSelectAll) {
      onSelectAll();
    }
  };

  return (
    <div className={`selection-bar ${className}`}>
      <div className="selection-bar-left">
        {onSelectAll && (
          <Checkbox
            checked={allSelected || allMatchingSelected}
            indeterminate={someSelected}
            onChange={handleSelectAllClick}
            ariaLabel={allSelected ? 'Deselect all' : selectAllLabel}
          />
        )}
        <span className="selection-bar-count">
          {selectedLabel.replace('{count}', selectedCount.toString())}
          {totalCount !== undefined && !allMatchingSelected && (
            <span className="selection-bar-total"> / {totalCount}</span>
          )}
        </span>
        {/* Cross-page select-all banner */}
        {showSelectAllBanner && onSelectAllMatching && totalMatching !== undefined && (
          <button
            className="selection-bar-select-all-matching"
            onClick={onSelectAllMatching}
            disabled={loadingAllIds}
          >
            {loadingAllIds
              ? '...'
              : selectAllMatchingLabel.replace('{count}', totalMatching.toString())}
          </button>
        )}
        {allMatchingSelected && (
          <span className="selection-bar-all-confirmed">
            {allSelectedConfirmLabel.replace('{count}', selectedCount.toString())}
          </span>
        )}
        {/* Action buttons - after selection counter */}
        <div className="selection-bar-actions">
          {onDownload && (
            <button
              className={`selection-bar-action ${!hasSelection ? 'selection-bar-action--disabled' : ''}`}
              onClick={onDownload}
              disabled={!hasSelection}
              aria-label={downloadLabel}
              data-tooltip={downloadLabel}
            >
              <Download className="selection-bar-action-icon" />
            </button>
          )}
          {onReprocess && !showDeleted && (
            <button
              className={`selection-bar-action ${!hasSelection ? 'selection-bar-action--disabled' : ''}`}
              onClick={onReprocess}
              disabled={!hasSelection}
              aria-label={reprocessLabel}
              data-tooltip={reprocessLabel}
            >
              <RefreshCw className="selection-bar-action-icon" />
            </button>
          )}
          {showDeleted ? (
            onRestore && (
              <button
                className={`selection-bar-action ${!hasSelection ? 'selection-bar-action--disabled' : ''}`}
                onClick={onRestore}
                disabled={!hasSelection}
                aria-label={restoreLabel}
                data-tooltip={restoreLabel}
              >
                <RotateCcw className="selection-bar-action-icon" />
              </button>
            )
          ) : (
            onDelete && (
              <button
                className={`selection-bar-action selection-bar-action--danger ${!hasSelection ? 'selection-bar-action--disabled' : ''}`}
                onClick={onDelete}
                disabled={!hasSelection}
                aria-label={deleteLabel}
                data-tooltip={deleteLabel}
              >
                <Trash2 className="selection-bar-action-icon" />
              </button>
            )
          )}
        </div>
      </div>
      <div className="selection-bar-right">
        {onCancel && (
          <button
            className="selection-bar-cancel"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default SelectionBar;
